// src/DeliveryScreen.jsx
import { useEffect, useMemo, useState, useRef } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import logo from "./assets/logo.png";
import { api } from "./api/api";
import { formatNumber } from "./utils/numberFormat";
import { AMIRI_BASE64 } from "./assets/fonts/Amiri-Regular.base64";

const formatDateToYMD = (dateString) => {
  if (!dateString || typeof dateString !== 'string') return "";
  return dateString.slice(0, 10);
};

const getTodayYMD = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = (today.getMonth() + 1).toString().padStart(2, '0');
  const day = today.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatAmount = (value) =>
  Number(value || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const ARABIC_RE = /[\u0600-\u06FF]/;
const SHIPMENT_CONDITION_GOOD = "Good Condition";
const SHIPMENT_CONDITION_BAD = "Bad Condition";

const normalizeShipmentCondition = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "bad" || normalized === "bad condition") return SHIPMENT_CONDITION_BAD;
  return SHIPMENT_CONDITION_GOOD;
};

const toPdfText = (doc, value) => {
  const text = value == null ? "" : String(value);
  if (!text) return "";
  if (ARABIC_RE.test(text) && typeof doc.processArabic === "function") {
    return doc.processArabic(text);
  }
  return text;
};

function generateInvoicePdf({ header, invoice, recipientName, deliveryDate, days, discount = 0, d_no }) {
  const doc = new jsPDF("p", "mm", "a4");
  const pageWidth = doc.internal.pageSize.getWidth();

  const logoWidth = 100;
  const logoHeight = 20;
  const logoX = (pageWidth - logoWidth) / 2;
  const logoY = 5;

  doc.addImage(logo, "PNG", logoX, logoY, logoWidth, logoHeight);

  let y = logoY + logoHeight + 20;

  doc.setFontSize(16);
  doc.text("Storage Charges Invoice", pageWidth / 2, y, { align: "center" });

  doc.setFontSize(11);
  y += 20;

  doc.text(`Voyage No.: ${header.voyage_number || ""}`, 80, y);
  doc.text(`Arrival Date: ${formatDateToYMD(header.date_of_arrival) || ""}`, 150, y);

  if (d_no) {
    doc.text(`Invoice No: ${d_no}`, 14, y);
  }
  y += 6;
  doc.text(`Days of Storage: ${days}`, 150, y);

  y += 6;
  const consigneeText = `Consignee / Receiver: ${recipientName || ""}`;
  const consigneeLines = doc.splitTextToSize(consigneeText, 130);
  doc.text(consigneeLines, 14, y);
  doc.text(`Delivery Date: ${formatDateToYMD(deliveryDate) || ""}`, 150, y);

  if (consigneeLines.length > 1) {
    const lineHeight = doc.getLineHeight() / doc.internal.scaleFactor;
    y += (consigneeLines.length - 1) * lineHeight;
  }

  if (invoice.service) {
    y += 6;
    doc.text(`Service: ${invoice.service.name}`, 14, y);
    doc.text(`Rate per day: ${formatAmount(invoice.service.price)}`, 150, y);
  }

  const finalAmount = invoice.total - discount;

  const tableBody = invoice.lines.map(
    ({ bol, unitPrice, qty, weight, amount }) => [
      bol.bol_reference || "",
      bol.goods_description || "",
      bol.consignee_name || "",
      formatNumber(qty || 0, 0),
      formatNumber(weight || 0, 2),
      formatNumber(unitPrice || 0, 2),
      days,
      formatNumber(finalAmount, 2),
    ]
  );

  autoTable(doc, {
    startY: y + 20,
    head: [
      [
        "AWB Ref.",
        "Goods Description",
        "Consignee",
        "Packages",
        "Weight",
        "Rate/day",
        "Days",
        "Total Amount",
      ],
    ],
    body: tableBody,
    styles: { fontSize: 8 },
    headStyles: { fillColor: [66, 165, 245], textColor: 255 },
  });

  const fileName = `ManifestInvoice_${header.id}.pdf`;
  doc.save(fileName);
}

// ✅ دالة POD المعدلة - إزالة POD ID و Consignee من الهيدر وإضافتهما للجدول
function generatePodPdf({
  header,
  bol,
  podId,
  doReceivedDate,
  doRecipient,
  cargoReceivedDate,
  cargoRecipient,
  shipmentCondition,
}) {
  const doc = new jsPDF("p", "mm", "a4");
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 14;

  doc.addFileToVFS("Amiri-Regular.ttf", AMIRI_BASE64);
  doc.addFont("Amiri-Regular.ttf", "Amiri", "normal");
  doc.setFont("Amiri", "normal");

  // Logo
  const logoWidth = 100;
  const logoHeight = 20;
  const logoX = (pageWidth - logoWidth) / 2;
  const logoY = 5;

  doc.addImage(logo, "PNG", logoX, logoY, logoWidth, logoHeight);

  // Title
  let y = logoY + logoHeight + 15;

  doc.setFontSize(16);
  doc.text("Proof of Delivery (POD)", pageWidth / 2, y, {
    align: "center",
  });

  doc.setFontSize(10);
  y += 10;

  const addWrappedLine = (label, value, maxWidth = 180) => {
    const text = `${label}: ${value || "-"}`;
    const lines = doc.splitTextToSize(text, maxWidth);
    doc.text(lines, margin, y);
    y += lines.length * 4.5 + 1.5;
  };

  const addTwoColumns = (
    label1,
    value1,
    label2,
    value2,
    colX = 100
  ) => {
    doc.text(`${label1}:`, margin, y);
    doc.text(toPdfText(doc, value1 || "-"), margin + 24, y);
    doc.text(`${label2}:`, colX, y);
    doc.text(toPdfText(doc, value2 || "-"), colX + 26, y);
    y += 5;
  };

  // Header - تم إزالة POD ID و Consignee من هنا
  addTwoColumns(
    "Voyage No",
    header?.voyage_number || "",
    "AWB Ref",
    bol?.bol_reference || "",
    100
  );

  y += 3;

  addTwoColumns(
    "DO Rec. Date",
    doReceivedDate,
    "DO Recipient",
    doRecipient,
    100
  );

  addTwoColumns(
    "Cargo Rec. Date",
    cargoReceivedDate,
    "Cargo Recipient",
    cargoRecipient,
    100
  );

  y += 3;

  addWrappedLine("Print Time", new Date().toLocaleString());
  addWrappedLine("Shipment Condition", shipmentCondition || "-");

  y += 5;

  // Table - تم إضافة POD ID و Consignee هنا
  const tableBody = [
    [
      podId || "",
      bol?.bol_reference || "",
      toPdfText(doc, bol?.consignee_name || ""),
      formatNumber(bol?.number_of_packages || 0, 0),
      formatNumber(bol?.gross_mass || 0, 2),
    ],
  ];

  autoTable(doc, {
    startY: y,
    head: [["POD ID", "AWB Ref.", "Consignee", "Pkgs", "Weight"]],
    body: tableBody,
    styles: {
      font: "Amiri",
      fontSize: 8,
      cellPadding: 2,
    },
    headStyles: {
      font: "Amiri",
      fillColor: [66, 165, 245],
      textColor: 255,
      fontSize: 8,
    },
    columnStyles: {
      0: { cellWidth: 30 },
      1: { cellWidth: 40 },
      2: { cellWidth: 50 },
      3: { cellWidth: 25, halign: "center" },
      4: { cellWidth: 30, halign: "center" },
    },
    margin: {
      left: margin,
      right: margin,
    },
  });

  // Signatures
  const finalY = doc.lastAutoTable.finalY + 15;

  doc.setFontSize(11);

  doc.text("_________________________", margin, finalY);
  doc.text("Delivered By (Signature)", margin, finalY + 6);

  doc.text(
    "_________________________",
    pageWidth - margin - 50,
    finalY
  );

  doc.text(
    "Received By (Signature)",
    pageWidth - margin - 50,
    finalY + 6
  );

  // Footer
  doc.setFontSize(8);

  doc.text(
    `POD ID: ${podId} | Printed: ${new Date().toLocaleString()}`,
    margin,
    285
  );

  const fileName = `POD_${bol?.bol_reference}_${podId}.pdf`;
  doc.save(fileName);
}

function DeliveryScreen({ onBack }) {
  const [pendingBols, setPendingBols] = useState([]);
  const [selectedBol, setSelectedBol] = useState(null);
  const [header, setHeader] = useState(null);
  const [days, setDays] = useState(1);

  const [services, setServices] = useState([]);
  const [selectedServiceId, setSelectedServiceId] = useState("");

  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const [recipientName, setRecipientName] = useState("");
  const [deliveryDate, setDeliveryDate] = useState(getTodayYMD());

  const [subtractOneDay, setSubtractOneDay] = useState(false);
  const [discount, setDiscount] = useState(0);

  const [doReceivedDate, setDoReceivedDate] = useState(getTodayYMD());
  const [doRecipient, setDoRecipient] = useState("");
  const [doRecipientPhone, setDoRecipientPhone] = useState("");
  const [cargoReceivedDate, setCargoReceivedDate] = useState("");
  const [cargoRecipient, setCargoRecipient] = useState("");
  const [shipmentCondition, setShipmentCondition] = useState(SHIPMENT_CONDITION_GOOD);
  const [showPodSection, setShowPodSection] = useState(false);
  const [podId, setPodId] = useState(null);
  const podIdRef = useRef(null);

  const [message, setMessage] = useState(null);

  useEffect(() => {
    podIdRef.current = podId;
  }, [podId]);

  useEffect(() => {
    const fetchPending = async () => {
      try {
        setLoading(true);
        const res = await api.get("/bols/pending");
        const list =
          Array.isArray(res) ? res :
          Array.isArray(res?.data) ? res.data :
          Array.isArray(res?.rows) ? res.rows :
          [];
        setPendingBols(list);
      } catch (err) {
        console.error(err);
        setPendingBols([]);
        setMessage({ type: "error", text: err.message || "Error loading pending AWBs" });
      } finally {
        setLoading(false);
      }
    };
    fetchPending();
  }, []);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const data = await api.get("/services");
        setServices(data);
      } catch (err) {
        console.error(err);
      }
    };
    fetchServices();
  }, []);

  useEffect(() => {
    if (!header || !header.date_of_arrival || !deliveryDate) return;

    const arrivalDateStr = formatDateToYMD(header.date_of_arrival);
    const deliveryDateStr = formatDateToYMD(deliveryDate);

    if (!arrivalDateStr || !deliveryDateStr) {
      setDays(1);
      return;
    }

    const [arrY, arrM, arrD] = arrivalDateStr.split('-').map(Number);
    const [delY, delM, delD] = deliveryDateStr.split('-').map(Number);

    const arrival = Date.UTC(arrY, arrM - 1, arrD);
    const delivery = Date.UTC(delY, delM - 1, delD);

    const diffMs = delivery - arrival;
    const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24)) + 1;

    setDays(diffDays < 1 ? 1 : diffDays);
  }, [header, deliveryDate]);

  const handleSelectBol = async (bolId) => {
    try {
      setLoading(true);
      setMessage(null);
      const data = await api.get(`/bols/${bolId}/details`);
      setHeader(data.header);
      setSelectedBol(data.bol || null);
      const defaultRecipient = data.bol?.consignee_name || "";
      setRecipientName(defaultRecipient);
      setDeliveryDate(getTodayYMD());
      setSelectedServiceId(data.bol && data.bol.service_id ? String(data.bol.service_id) : "");
      setDays(data.days || 1);

      setDoReceivedDate(getTodayYMD());
      setDoRecipient("");
      setDoRecipientPhone("");
      setCargoReceivedDate("");
      setCargoRecipient("");
      setShipmentCondition(SHIPMENT_CONDITION_GOOD);
      setShowPodSection(false);
      setPodId(null);
      podIdRef.current = null;

      setSubtractOneDay(!!data.bol.day);
      setDiscount(0);

      setModalOpen(true);
    } catch (err) {
      console.error(err);
      setMessage({ type: "error", text: err.message || "Error loading AWB details" });
    } finally {
      setLoading(false);
    }
  };

  const handleDeliverBol = async () => {
    if (!selectedBol || !header) return;

    if (!selectedServiceId) {
      setMessage({ type: "error", text: "Please select a service before generating the invoice" });
      return;
    }

    try {
      setLoading(true);
      setMessage(null);

      const finalDays = Math.max(1, subtractOneDay ? days - 1 : days);

      const data = await api.post(`/bols/${selectedBol.id}/deliver`, {
        recipientName,
        deliveryDate,
        serviceId: Number(selectedServiceId),
        days: finalDays,
        discount: Number(discount) || 0,
        day: subtractOneDay ? 1 : 0,
      });

      try {
        const invoice = {
          service: data.service,
          total: data.total,
          lines: [
            {
              bol: {
                bol_reference: data.bol.bol_reference,
                goods_description: data.bol.goods_description,
                consignee_name: data.bol.consignee_name,
              },
              unitPrice: data.line.unitPrice,
              qty: data.bol.number_of_packages,
              weight: data.bol.gross_mass,
              amount: data.line.amount,
            },
          ],
        };

        generateInvoicePdf({
          header: data.header,
          invoice,
          recipientName: data.recipientName,
          deliveryDate: data.deliveryDate,
          days: finalDays,
          discount: discount,
          d_no: data.bol.d_no,
        });
      } catch (pdfErr) {
        console.error("PDF generation error:", pdfErr);
      }

      setMessage({ type: "success", text: "Invoice generated successfully ✅. Fill POD details and print." });

      setShowPodSection(true);
      setDoReceivedDate(getTodayYMD());
      setDoRecipient(data.bol?.do_recipient || "");
      setDoRecipientPhone(data.bol?.do_recipient_phone || "");
      setCargoReceivedDate(formatDateToYMD(data.bol?.delivery_date) || getTodayYMD());
      setCargoRecipient(data.recipientName || data.bol?.delivered_to || data.bol?.consignee_name || "");
      setShipmentCondition(SHIPMENT_CONDITION_GOOD);
      setSelectedBol(prev => ({
        ...prev,
        is_delivered: 1,
        receipt_no: data.bol.receipt_no,
        receipt_amount: data.bol.receipt_amount,
        d_no: data.bol.d_no,
        invoice_amount: data.total,
      }));
      setPendingBols((prev) => prev.filter((row) => Number(row.bol_id) !== Number(selectedBol.id)));

    } catch (err) {
      console.error(err);
      if (err?.status === 409) {
        setMessage({ type: "error", text: err?.message || "Insufficient stock for this AWB" });
      } else {
        setMessage({ type: "error", text: err?.message || "Server connection error during delivery" });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!showPodSection || !selectedBol?.id) return;

    const preloadExistingPod = async () => {
      try {
        const existing = await api.get(`/bols/${selectedBol.id}/pod`);
        const podData = existing?.data || existing;
        if (!podData || !podData.id) return;

        setPodId(podData.id);
        podIdRef.current = podData.id;
        setDoReceivedDate((prev) => prev || formatDateToYMD(podData.do_received_date) || getTodayYMD());
        setDoRecipient((prev) => prev || podData.do_recipient || "");
        setDoRecipientPhone((prev) => prev || podData.do_recipient_phone || "");
        setCargoReceivedDate((prev) => prev || formatDateToYMD(podData.cargo_received_date) || "");
        setCargoRecipient((prev) => prev || podData.cargo_recipient || "");
      } catch (e) {
        // No existing POD found; keep local defaults.
      }
    };

    preloadExistingPod();
  }, [showPodSection, selectedBol?.id]);

  // ✅ handlePrintPod المعدل - معالجة أفضل لخطأ 404
const handlePrintPod = async () => {
  if (!selectedBol || !header) return;

  const normalizedDoReceivedDate = String(doReceivedDate || "").trim() || getTodayYMD();
  const normalizedShipmentCondition = normalizeShipmentCondition(shipmentCondition);

  if (!doRecipient || !cargoReceivedDate || !cargoRecipient) {
    setMessage({ type: "error", text: "Please fill all POD fields" });
    return;
  }

  try {
    setLoading(true);
    setMessage(null);

    let currentPodId = podIdRef.current;

    if (!currentPodId) {
      // محاولة جلب POD موجود
      try {
        const existing = await api.get(`/bols/${selectedBol.id}/pod`);
        // ✅ التحقق من البيانات بشكل صحيح
        const podData = existing?.data || existing;
        if (podData && podData.id) {
          currentPodId = podData.id;
          setPodId(currentPodId);
          podIdRef.current = currentPodId;
          // ملء الحقول من البيانات الموجودة إذا كانت فارغة
          setDoReceivedDate(prev => prev || formatDateToYMD(podData.do_received_date) || getTodayYMD());
          setDoRecipient(prev => prev || podData.do_recipient || "");
          setDoRecipientPhone(prev => prev || podData.do_recipient_phone || "");
          setCargoReceivedDate(prev => prev || formatDateToYMD(podData.cargo_received_date) || "");
          setCargoRecipient(prev => prev || podData.cargo_recipient || "");
        }
      } catch (e) {
        // 404 أو أي خطأ - لا يوجد POD سابق، نستمر لإنشاء واحد جديد
        console.log("No existing POD found, will create new one");
      }

      // إنشاء POD جديد إذا لم يتم العثور على موجود
      if (!currentPodId) {
        const res = await api.post(`/bols/${selectedBol.id}/pod`, {
          doReceivedDate: normalizedDoReceivedDate,
          doRecipient,
          doRecipientPhone,
          cargoReceivedDate,
          cargoRecipient,
        });

        currentPodId = res?.pod_id || res?.id || res?.data?.id || res?.data?.pod_id;
        setPodId(currentPodId);
        podIdRef.current = currentPodId;
      }
    }

    // طباعة POD
    generatePodPdf({
      header,
      bol: selectedBol,
      podId: currentPodId,
      doReceivedDate: normalizedDoReceivedDate,
      doRecipient,
      cargoReceivedDate,
      cargoRecipient,
      shipmentCondition: normalizedShipmentCondition,
    });

    setMessage({ type: "success", text: `POD printed ✅ (ID: ${currentPodId})` });
  } catch (err) {
    console.error("POD Error:", err);
    // معالجة خطأ 409 (POD موجود مسبقاً)
    if (err.status === 409 && (err.pod_id || err.data?.pod_id)) {
      const existingPodId = err.pod_id || err.data?.pod_id;
      setPodId(existingPodId);
      podIdRef.current = existingPodId;
      setMessage({ type: "warning", text: "POD already exists, reprinting..." });
      generatePodPdf({
        header,
        bol: selectedBol,
        podId: existingPodId,
        doReceivedDate: normalizedDoReceivedDate,
        doRecipient,
        cargoReceivedDate,
        cargoRecipient,
        shipmentCondition: normalizedShipmentCondition,
      });
    } else {
      setMessage({ type: "error", text: err.message || "Error generating POD" });
    }
  } finally {
    setLoading(false);
  }
};

  const [searchAwb, setSearchAwb] = useState("");

  const filteredPending = useMemo(() => {
    const list = Array.isArray(pendingBols) ? pendingBols : [];
    const q = searchAwb.trim().toLowerCase();
    if (!q) return list;
    return list.filter((b) =>
      String(b.bol_reference || "").toLowerCase().includes(q)
    );
  }, [pendingBols, searchAwb]);

  const finalDays = useMemo(() => {
    const d = subtractOneDay ? days - 1 : days;
    return Math.max(1, d);
  }, [days, subtractOneDay]);

  return (
    <div className="screen screen-rtl">
      <div className="screen-header">
        <h2>Delivery / Invoice</h2>
      </div>

      {message && (
        <div className={`message ${message.type === "error" ? "error" : "success"}`}>
          {message.text}
        </div>
      )}

      <div className="card">
        <div className="grid grid-auto small-gap">
          <div className="field">
            <label>Search by AWB</label>
            <input
              value={searchAwb}
              onChange={(e) => setSearchAwb(e.target.value)}
              placeholder="Type AWB reference..."
            />
          </div>
          <div className="field">
            <label>Results</label>
            <input value={filteredPending.length} readOnly />
          </div>
          <div className="field" style={{ alignSelf: "end" }}>
            <button type="button" onClick={() => setSearchAwb("")}>
              Clear
            </button>
          </div>
        </div>

        <h3>Pending AWBs</h3>

        {loading && <p>Loading...</p>}
        {!loading && pendingBols.length === 0 && (
          <p className="hint-text">No pending BOLs.</p>
        )}

        {!loading && pendingBols.length > 0 && (
          <div className="table-scroll-y">
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>AWB</th>
                    <th>Consignee</th>
                    <th>Packages</th>
                    <th>Gross Weight</th>
                    <th>Voyage No.</th>
                    <th>Arrival Date</th>
                    <th>From</th>
                    <th>To</th>
                    <th>Invoice</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPending.map((row) => (
                    <tr key={row.bol_id}>
                      <td>{row.bol_reference}</td>
                      <td>{row.consignee_name}</td>
                      <td>{row.number_of_packages}</td>
                      <td>{row.gross_mass}</td>
                      <td>{row.voyage_number}</td>
                      <td>{formatDateToYMD(row.date_of_arrival)}</td>
                      <td>{row.place_of_departure_code}</td>
                      <td>{row.place_of_destination_code}</td>
                      <td>
                        <button type="button" onClick={() => handleSelectBol(row.bol_id)}>
                          Generate Invoice
                        </button>
                      </td>
                    </tr>
                  ))}
                  {!loading && filteredPending.length === 0 && (
                    <tr>
                      <td colSpan={9} className="hint-text">No matching AWBs.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {modalOpen && header && selectedBol && (
        <div className="modal-backdrop">
          <div className="modal">
            <div className="modal-header">
              <h3>Invoice for AWB #{selectedBol.bol_reference}</h3>
              <button type="button" className="secondary-btn" onClick={() => setModalOpen(false)}>
                Close
              </button>
            </div>
            <div className="modal-body">
              <div className="card">
                <h4>Header Information</h4>
                <div className="grid grid-auto small-gap">
                  <div><strong>Voyage No.:</strong> {header.voyage_number}</div>
                  <div><strong>Arrival Date:</strong> {formatDateToYMD(header.date_of_arrival)}</div>
                  <div><strong>From:</strong> {header.place_of_departure_code}</div>
                  <div><strong>To:</strong> {header.place_of_destination_code}</div>
                  <div><strong>Days:</strong> {finalDays}</div>
                </div>
              </div>

              <div className="card">
                <h4>Delivery & Service</h4>
                <div className="grid grid-auto small-gap">
                  <div className="field">
                    <input
                      type="text"
                      value={recipientName}
                      onChange={(e) => setRecipientName(e.target.value)}
                      placeholder=" "
                    />
                    <label>Receiver Name (default: consignee)</label>
                  </div>
                  <div className="field">
                    <input
                      type="date"
                      value={deliveryDate}
                      onChange={(e) => setDeliveryDate(e.target.value)}
                      placeholder=" "
                    />
                    <label>Delivery Date</label>
                  </div>
                  <div className="field select-field">
                    <select
                      value={selectedServiceId}
                      onChange={(e) => setSelectedServiceId(e.target.value)}
                    >
                      <option value="">Select Service</option>
                      {services.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                    <label>Service</label>
                  </div>
                </div>
                <div className="grid grid-auto small-gap" style={{ marginTop: '1rem', alignItems: 'center' }}>
                  <div className="field-checkbox">
                    <input
                      id="subtract-day-ch"
                      type="checkbox"
                      checked={subtractOneDay}
                      onChange={(e) => setSubtractOneDay(e.target.checked)}
                    />
                    <label htmlFor="subtract-day-ch">Subtract one day from total</label>
                  </div>
                  <div className="field">
                    <input
                      type="number"
                      value={discount}
                      onChange={(e) => setDiscount(Number(e.target.value) || 0)}
                      placeholder=" "
                      style={{ textAlign: 'right' }}
                    />
                    <label>Discount Amount</label>
                  </div>
                </div>
              </div>

              <div className="card">
                <h4>AWB Details (Estimated)</h4>

                <div className="table-scroll-y modal-table-scroll">
                  <div className="table-wrap"></div>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>AWB</th>
                        <th>Goods Description</th>
                        <th>Consignee</th>
                        <th>Days</th>
                        <th>Packages</th>
                        <th>Gross Weight</th>
                        <th>Rate/Day</th>
                        <th>Estimated Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const service = services.find((s) => s.id === Number(selectedServiceId));
                        const unitPrice = service ? Number(service.price) : 0;
                        const weight = Number(selectedBol.gross_mass) || 0;
                        const qty = Number(selectedBol.number_of_packages) || 0;
                        const amount = unitPrice * finalDays * weight;

                        return (
                          <tr>
                            <td>{selectedBol.bol_reference}</td>
                            <td>{selectedBol.goods_description}</td>
                            <td>{selectedBol.consignee_name}</td>
                            <td>{finalDays}</td>
                            <td>{formatNumber(qty, 0)}</td>
                            <td>{formatNumber(weight, 2)}</td>
                            <td>{formatNumber(unitPrice, 2)}</td>
                            <td>{formatNumber(amount, 2)}</td>
                          </tr>
                        );
                      })()}
                    </tbody>
                  </table>
                </div>

                {(() => {
                  const service = services.find((s) => s.id === Number(selectedServiceId));
                  const unitPrice = service ? Number(service.price) : 0;
                  const weight = Number(selectedBol.gross_mass) || 0;
                  const amount = unitPrice * finalDays * weight;
                  const totalAfterDiscount = amount - discount;
                  return (
                    <div style={{ textAlign: 'right', marginTop: '1rem', paddingRight: '1rem' }}>
                      <h4>Total Amount: {formatNumber(totalAfterDiscount, 2)}</h4>
                    </div>
                  )
                })()}

                {showPodSection && (
                  <div className="card" style={{ marginTop: '1rem', border: '2px solid #4caf50' }}>
                    <h4 style={{ color: '#4caf50' }}>📝 Proof of Delivery (POD)</h4>
                    <div className="grid grid-auto small-gap">
                      <div className="field">
                        <input
                          type="text"
                          value={doRecipient}
                          onChange={(e) => setDoRecipient(e.target.value)}
                          placeholder=" "
                        />
                        <label>DO Recipient *</label>
                      </div>
                      <div className="field">
                        <input
                          type="text"
                          value={doRecipientPhone}
                          onChange={(e) => setDoRecipientPhone(e.target.value)}
                          placeholder=" "
                        />
                        <label>DO Recipient Phone</label>
                      </div>
                      <div className="field">
                        <input
                          type="date"
                          value={cargoReceivedDate}
                          onChange={(e) => setCargoReceivedDate(e.target.value)}
                          placeholder=" "
                        />
                        <label>Cargo Received Date *</label>
                      </div>
                      <div className="field">
                        <input
                          type="text"
                          value={cargoRecipient}
                          onChange={(e) => setCargoRecipient(e.target.value)}
                          placeholder=" "
                        />
                        <label>Cargo Recipient *</label>
                      </div>
                      <div className="field" style={{ gridColumn: "1 / -1" }}>
                        <select
                          value={shipmentCondition}
                          onChange={(e) => setShipmentCondition(e.target.value)}
                        >
                          <option value={SHIPMENT_CONDITION_GOOD}>{SHIPMENT_CONDITION_GOOD}</option>
                          <option value={SHIPMENT_CONDITION_BAD}>{SHIPMENT_CONDITION_BAD}</option>
                        </select>
                        <label>Shipment Condition (for POD print only)</label>
                      </div>
                    </div>
                    {podId && (
                      <div style={{ marginTop: '1rem', padding: '0.5rem', background: '#e8f5e9', borderRadius: '4px' }}>
                        <strong>POD ID: {podId}</strong>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="modal-footer">
              {!showPodSection ? (
                <button
                  type="button"
                  onClick={handleDeliverBol}
                  disabled={loading || !selectedServiceId}
                >
                  {loading ? "Processing..." : "Generate Invoice"}
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={handlePrintPod}
                    disabled={loading || !doRecipient || !cargoReceivedDate || !cargoRecipient}
                  >
                    {loading ? "Processing..." : "🖨️ Print & Save POD"}
                  </button>
                  <button
                    type="button"
                    className="secondary-btn"
                    onClick={() => {
                      setModalOpen(false);
                      setSelectedBol(null);
                      setHeader(null);
                      setShowPodSection(false);
                      setPodId(null);
                      podIdRef.current = null;
                      setPendingBols((prev) => prev.filter((row) => row.bol_id !== selectedBol.id));
                    }}
                  >
                    Close
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DeliveryScreen;